import { useEffect, useState } from "react";
import {
  Plus,
  Users as UsersIcon,
  Mail,
  Store,
  Pencil,
  Trash2,
  User as UserIcon,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
  Bell,
  CreditCard,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../../../lib/auth-context";
import { api, getErrorMessage } from "../../../lib/api";
import { withProfileExtras, setProfileExtras, getProfileExtras } from "../../../lib/local-profile";

const emptyForm = { name: "", email: "", sellerName: "", password: "", userType: 3 };

const STATUS_LABEL = { 0: "Active", 1: "Inactive" };
const TYPE_LABEL = { 1: "Admin", 2: "Manager", 3: "User" };

export const UsersPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.userType === 1;
  const isManager = user?.userType === 2;
  const canView = isAdmin || isManager;
  const canManage = isAdmin;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [remindingId, setRemindingId] = useState(null);

  // Manage plan
  const [planTarget, setPlanTarget] = useState(null); // { id, name }
  const [planId, setPlanId] = useState("trial");
  const [planActivating, setPlanActivating] = useState(false);

  const openPlanDialog = (u) => {
    setPlanTarget(u);
    setPlanId("trial");
  };

  const activatePlan = async () => {
    if (!planTarget) return;
    setPlanActivating(true);
    try {
      await api.post("/protected/subscription/admin-activate", { userId: planTarget.id, planId });
      toast.success(`${planId.charAt(0).toUpperCase() + planId.slice(1)} plan activated for ${planTarget.name}`);
      setPlanTarget(null);
    } catch (err) {
      toast.error("Failed to activate plan", { description: getErrorMessage(err) });
    } finally {
      setPlanActivating(false);
    }
  };

  const sendReminder = async (userId) => {
    setRemindingId(userId);
    try {
      await api.post("/protected/send-reminder", { userId });
      toast.success("Reminder sent!");
    } catch (err) {
      if (err?.response?.status === 422) {
        toast.error("No devices registered", { description: "This user hasn't enabled notifications yet." });
      } else {
        toast.error("Failed to send reminder", { description: getErrorMessage(err) });
      }
    } finally {
      setRemindingId(null);
    }
  };

  const isEdit = Boolean(editingId);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setList(data.users.filter((u) => u.id !== user.id).map(withProfileExtras));
    } catch (err) {
      toast.error("Couldn't load users", { description: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowPw(false);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name,
      email: u.email,
      sellerName: u.sellerName ?? getProfileExtras(u.id).sellerName ?? "",
      password: "",
      userType: u.userType,
    });
    setShowPw(false);
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit) {
        const patch = {
          userId: editingId,
          name: form.name,
          email: form.email,
          userType: form.userType,
          sellerName: form.sellerName,
        };
        if (form.password) patch.password = form.password;
        await api.put("/auth/update-user", patch);
        setProfileExtras(editingId, { sellerName: form.sellerName });
        toast.success("User updated");
      } else {
        const { data } = await api.post("/auth/create-user", {
          name: form.name,
          email: form.email,
          userType: form.userType,
          password: form.password,
          sellerName: form.sellerName,
        });
        if (form.sellerName) setProfileExtras(data.user.id, { sellerName: form.sellerName });
        toast.success("User created");
      }
      setDialogOpen(false);
      await loadUsers();
    } catch (err) {
      toast.error(isEdit ? "Update failed" : "Create failed", { description: getErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/delete-user", { data: { userId: deletingId } });
      toast.success("User deleted");
      setDeletingId(null);
      await loadUsers();
    } catch (err) {
      toast.error("Delete failed", { description: getErrorMessage(err) });
    } finally {
      setDeleting(false);
    }
  };

  const ownerInitial = (user?.name?.[0] || "S").toUpperCase();

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="panel-users">
        <div className="rounded-3xl border border-purple-100 bg-white p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-4">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="font-heading text-lg font-extrabold text-slate-900">Admins & Managers only</div>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
            Team & sub-user visibility is restricted to Admin and Manager accounts. Your account is a{" "}
            <strong>{TYPE_LABEL[user?.userType] || "User"}</strong> account.
          </p>
        </div>
      </div>
    );
  }

  /* Shared action buttons — icon-only remind, text for edit/delete */
  const ActionButtons = ({ u }) => (
    <div className="flex items-center gap-1">
      <button
        onClick={() => openEdit(u)}
        data-testid={`user-edit-${u.id}`}
        className="inline-flex items-center gap-1 px-2 h-7 rounded-lg text-[11px] font-semibold text-purple-700 hover:bg-purple-50"
      >
        <Pencil className="w-3 h-3" /> Edit
      </button>
      <button
        onClick={() => openPlanDialog(u)}
        data-testid={`user-plan-${u.id}`}
        title="Manage subscription plan"
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-violet-500 hover:bg-violet-50"
      >
        <CreditCard className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => sendReminder(u.id)}
        disabled={remindingId === u.id}
        data-testid={`user-remind-${u.id}`}
        title="Send reminder notification"
        className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-amber-500 hover:bg-amber-50 disabled:opacity-40"
      >
        <Bell className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => setDeletingId(u.id)}
        data-testid={`user-delete-${u.id}`}
        className="inline-flex items-center gap-1 px-2 h-7 rounded-lg text-[11px] font-semibold text-red-500 hover:bg-red-50"
      >
        <Trash2 className="w-3 h-3" /> Delete
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="panel-users">
      {/* Header */}
      <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-lg shadow-purple-600/25 shrink-0">
            <UsersIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-lg font-extrabold text-slate-900">Team & sub-users</div>
            <div className="text-sm text-slate-600 mt-0.5 max-w-md">
              {canManage
                ? "Manage your team. Active users can log in and use EasyLister."
                : "View your team. Ask an Admin to add, edit, or remove members."}
            </div>
          </div>
        </div>
        {canManage && (
          <Button
            onClick={openCreate}
            data-testid="users-create-button"
            className="w-full sm:w-auto rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-lg shadow-purple-600/25"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create User
          </Button>
        )}
      </div>

      {/* ── MOBILE card list (hidden on md+) ── */}
      <div className="md:hidden space-y-3" data-testid="users-cards">
        {/* Owner card */}
        <div className="rounded-2xl border border-purple-100 bg-purple-50/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center font-bold text-sm shrink-0">
              {ownerInitial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm">{user?.name || "You"}</span>
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full text-[10px] font-bold">
                  {TYPE_LABEL[user?.userType] || "Admin"}
                </Badge>
                <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> You
                </span>
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                <Mail className="w-3 h-3 shrink-0" />
                <span className="truncate">{user?.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-user cards */}
        {list.map((u) => (
          <div key={u.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`user-row-${u.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm shrink-0">
                {u.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">{u.name}</span>
                  <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full text-[10px] font-bold px-2 py-0">
                    {TYPE_LABEL[u.userType] || "User"}
                  </Badge>
                  <span className={`text-[11px] font-bold ${u.status === 0 ? "text-emerald-600" : "text-slate-400"}`}>
                    {STATUS_LABEL[u.status] || "Active"}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                  <Mail className="w-3 h-3 shrink-0" />
                  <span className="truncate">{u.email}</span>
                </div>
                {u.sellerName && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-400">
                    <Store className="w-3 h-3 shrink-0" />
                    <span className="truncate">{u.sellerName}</span>
                  </div>
                )}
                {u.createdAt && (
                  <div className="mt-0.5 text-[10px] text-slate-400">
                    Joined {new Date(u.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
            </div>
            {canManage && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <ActionButtons u={u} />
              </div>
            )}
          </div>
        ))}

        {!loading && list.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center">
            <div className="mx-auto w-10 h-10 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-3">
              <UsersIcon className="w-5 h-5" />
            </div>
            <div className="font-heading text-sm font-bold text-slate-900">No sub-users yet</div>
            <div className="text-xs text-slate-500 mt-1 max-w-xs mx-auto px-4">
              {canManage
                ? <>Tap <strong>Create User</strong> to add your first team member.</>
                : "An Admin hasn't added any team members yet."}
            </div>
          </div>
        )}
      </div>

      {/* ── DESKTOP table (hidden below md) ── */}
      <div className="hidden md:block rounded-3xl border border-slate-200 bg-white overflow-hidden" data-testid="users-table-card">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-heading text-sm font-extrabold text-slate-900">
            {list.length + 1} member{list.length + 1 > 1 ? "s" : ""}
          </div>
          <div className="text-xs text-slate-500">Owner + sub-users</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100 bg-slate-50/40">
              <tr>
                <th className="py-3 px-6">Name</th>
                <th className="py-3 px-6">Email</th>
                <th className="py-3 px-6">Status</th>
                <th className="py-3 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Owner row */}
              <tr className="bg-purple-50/20" data-testid="user-row-owner">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center font-bold text-sm shrink-0">
                      {ownerInitial}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                        {user?.name || "You"}
                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full text-[10px] font-bold">
                          {TYPE_LABEL[user?.userType] || "Admin"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6 text-slate-600 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    {user?.email}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700">
                    <ShieldCheck className="w-3.5 h-3.5" /> Active · You
                  </span>
                </td>
                <td className="py-4 px-6 text-right text-xs text-slate-400">— You —</td>
              </tr>

              {/* Sub-users */}
              {list.map((u) => (
                <tr key={u.id} className="hover:bg-purple-50/20" data-testid={`user-row-${u.id}`}>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm shrink-0">
                        {u.name?.[0]?.toUpperCase() || "U"}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{u.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full text-[10px] font-bold px-2 py-0">
                            {TYPE_LABEL[u.userType] || "User"}
                          </Badge>
                          {u.sellerName && (
                            <>
                              <span>·</span>
                              <Store className="w-3 h-3 shrink-0" />
                              <span className="truncate">{u.sellerName}</span>
                            </>
                          )}
                          {u.createdAt && (
                            <span className="text-[10px] text-slate-400 shrink-0">
                              · Joined {new Date(u.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-slate-600 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      {u.email}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`text-xs font-bold ${u.status === 0 ? "text-emerald-700" : "text-slate-400"}`}>
                      {STATUS_LABEL[u.status] || "Active"}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    {canManage ? (
                      <div className="flex items-center justify-end">
                        <ActionButtons u={u} />
                      </div>
                    ) : (
                      <div className="text-right text-xs text-slate-400">View only</div>
                    )}
                  </td>
                </tr>
              ))}

              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center">
                    <div className="mx-auto w-12 h-12 rounded-2xl bg-purple-50 text-purple-700 flex items-center justify-center mb-3">
                      <UsersIcon className="w-5 h-5" />
                    </div>
                    <div className="font-heading text-sm font-bold text-slate-900">No sub-users yet</div>
                    <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                      {canManage
                        ? <><strong>Create User</strong> to add your first team member.</>
                        : "An Admin hasn't added any team members yet."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="user-dialog"
          className="sm:max-w-md rounded-3xl border-purple-100 p-0 overflow-hidden"
        >
          <DialogHeader className="px-6 sm:px-8 pt-7 pb-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center shadow-lg shadow-purple-600/30 shrink-0">
                {isEdit ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </div>
              <div>
                <DialogTitle className="font-heading text-lg font-extrabold text-slate-900 leading-tight">
                  {isEdit ? "Edit user" : "Create new user"}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-1">
                  {isEdit
                    ? "Update this team member's details."
                    : "They'll get a separate login to access EasyLister."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={submit} className="px-6 sm:px-8 pb-7 pt-3 space-y-4" data-testid="user-form">
            <div>
              <Label htmlFor="u-name" className="text-sm font-semibold text-slate-700">Name</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <UserIcon className="w-4 h-4" />
                </span>
                <Input
                  id="u-name"
                  data-testid="user-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Full name"
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="u-email" className="text-sm font-semibold text-slate-700">Email</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Mail className="w-4 h-4" />
                </span>
                <Input
                  id="u-email"
                  data-testid="user-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="user@example.com"
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="u-seller" className="text-sm font-semibold text-slate-700">
                Seller / Store Name <span className="text-slate-400 font-normal">· Optional</span>
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Store className="w-4 h-4" />
                </span>
                <Input
                  id="u-seller"
                  data-testid="user-seller-input"
                  value={form.sellerName}
                  onChange={(e) => setField("sellerName", e.target.value)}
                  placeholder="Their store / brand name"
                  className="h-11 pl-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="u-type" className="text-sm font-semibold text-slate-700">Role</Label>
              <select
                id="u-type"
                data-testid="user-type-select"
                value={form.userType}
                onChange={(e) => setField("userType", Number(e.target.value))}
                className="mt-1.5 w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus-visible:ring-purple-500 focus-visible:ring-2 focus:outline-none"
              >
                <option value={3}>User</option>
                <option value={2}>Manager</option>
                <option value={1}>Admin</option>
              </select>
            </div>

            <div>
              <Label htmlFor="u-pw" className="text-sm font-semibold text-slate-700">
                Password {isEdit && <span className="text-slate-400 font-normal">· leave blank to keep current</span>}
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Lock className="w-4 h-4" />
                </span>
                <Input
                  id="u-pw"
                  data-testid="user-password-input"
                  type={showPw ? "text" : "password"}
                  required={!isEdit}
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  placeholder={isEdit ? "Leave blank to keep current" : "Password"}
                  className="h-11 pl-10 pr-10 rounded-xl border-slate-200 focus-visible:ring-purple-500 focus-visible:ring-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  data-testid="user-password-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-500 hover:text-purple-700 hover:bg-purple-50"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="user-cancel-button"
                className="rounded-full border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold h-11 px-5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="user-submit-button"
                className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold h-11 px-6 shadow-lg shadow-purple-600/25"
              >
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage plan dialog */}
      <Dialog open={Boolean(planTarget)} onOpenChange={(o) => !o && setPlanTarget(null)}>
        <DialogContent className="sm:max-w-sm rounded-3xl border-purple-100 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-purple-700 text-white flex items-center justify-center shadow-lg shadow-violet-600/30 shrink-0">
                <CreditCard className="w-4 h-4" />
              </div>
              <div>
                <DialogTitle className="font-heading text-base font-extrabold text-slate-900 leading-tight">
                  Manage subscription
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">
                  {planTarget?.name} — select a plan to activate
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 pb-6 pt-3 space-y-4">
            <div>
              <Label htmlFor="plan-select" className="text-sm font-semibold text-slate-700">Plan</Label>
              <select
                id="plan-select"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="mt-1.5 w-full h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="trial">Trial — 3 days (free)</option>
                <option value="starter">Starter — 30 days</option>
                <option value="growth">Growth — 30 days</option>
                <option value="pro">Pro — 30 days</option>
              </select>
            </div>

            <div className="rounded-xl bg-violet-50 border border-violet-100 px-4 py-3 text-xs text-violet-700 leading-relaxed">
              This will <strong>cancel any existing active subscription</strong> for this user and immediately start the selected plan.
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPlanTarget(null)}
                className="flex-1 rounded-full border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold h-10"
              >
                Cancel
              </Button>
              <Button
                onClick={activatePlan}
                disabled={planActivating}
                className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold h-10 shadow-lg shadow-violet-600/25"
              >
                {planActivating ? "Activating…" : "Activate plan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={Boolean(deletingId)} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent data-testid="user-delete-confirm" className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The user will immediately lose access to EasyLister.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="user-delete-confirm-button"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
